import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight, BadgeCheck, CalendarCheck, FileCheck, Mail, MapPin, Menu, Phone, ShieldCheck, Sun, X,
} from "lucide-react";
import { Logo } from "./Logo";
import { CATEGORIES } from "@/lib/api";

export function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const navLinkClass = ({ isActive }) =>
  `text-sm font-semibold transition-colors ${
    isActive ? "text-terra" : "text-forest-700 hover:text-terra"
  }`;

function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mt-3 flex items-center justify-between rounded-xl border border-forest-700/10 bg-bone/85 px-4 py-3 shadow-sm backdrop-blur-xl">
          <Link to="/" data-testid="nav-logo" aria-label="Melwest Eco Energy home">
            <Logo />
          </Link>
          <nav className="hidden items-center gap-6 lg:flex">
            {CATEGORIES.map((c) => (
              <NavLink key={c.slug} to={`/products/${c.slug}`} className={navLinkClass} data-testid={`nav-link-${c.slug}`}>
                {c.name}
              </NavLink>
            ))}
            <NavLink to="/rebates" className={navLinkClass} data-testid="nav-link-rebates">Rebates</NavLink>
            <NavLink to="/installers" className={navLinkClass} data-testid="nav-link-installers">Our Installers</NavLink>
            <NavLink to="/trade-stcs" className={navLinkClass} data-testid="nav-link-trade-stcs">Trade STCs</NavLink>
            <NavLink to="/faq" className={navLinkClass} data-testid="nav-link-faq">FAQ</NavLink>
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-get-quote"
              className="hidden items-center gap-2 rounded-full bg-terra px-5 py-2.5 text-sm font-bold text-bone transition-colors hover:bg-terra-dark sm:inline-flex"
            >
              Get a Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <button
              className="inline-flex h-10 w-10 items-center justify-center rounded-full text-forest-700 transition-colors hover:bg-sage lg:hidden"
              data-testid="mobile-menu-button"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="grain fixed inset-0 z-[-1] flex flex-col justify-center bg-forest-800 px-8 lg:hidden"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-1">
              {CATEGORIES.map((c) => (
                <Link
                  key={c.slug}
                  to={`/products/${c.slug}`}
                  data-testid={`mobile-link-${c.slug}`}
                  className="font-display border-b border-bone/10 py-3 text-2xl font-bold text-bone transition-colors hover:text-terra"
                >
                  {c.name}
                </Link>
              ))}
              <Link to="/rebates" data-testid="mobile-link-rebates" className="font-display border-b border-bone/10 py-3 text-2xl font-bold text-bone hover:text-terra">Rebates</Link>
              <Link to="/installers" data-testid="mobile-link-installers" className="font-display border-b border-bone/10 py-3 text-2xl font-bold text-bone hover:text-terra">Our Installers</Link>
              <Link to="/trade-stcs" data-testid="mobile-link-trade-stcs" className="font-display border-b border-bone/10 py-3 text-2xl font-bold text-bone hover:text-terra">Trade STCs</Link>
              <Link to="/faq" data-testid="mobile-link-faq" className="font-display border-b border-bone/10 py-3 text-2xl font-bold text-bone hover:text-terra">FAQ</Link>
              <Link
                to="/contact"
                data-testid="mobile-link-quote"
                className="mt-6 inline-flex w-max items-center gap-2 rounded-full bg-terra px-7 py-3.5 text-sm font-bold text-bone"
              >
                Get a Quote <ArrowRight className="h-4 w-4" />
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

const BADGES = [
  { icon: ShieldCheck, label: "Solar Victoria authorised retailer" },
  { icon: BadgeCheck, label: "Clean Energy Regulator accredited" },
  { icon: FileCheck, label: "VEU aggregator" },
  { icon: Sun, label: "STC certificate creator" },
];

function Footer() {
  return (
    <footer className="grain bg-forest-800 text-bone">
      <div className="relative z-10 mx-auto max-w-7xl px-6 pb-10 pt-16">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <Logo light size="lg" />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-bone/70">
              Heat pumps, air conditioning, solar and batteries for Victorian
              homes — with every rebate you're owed, priced into your quote.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {BADGES.map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 rounded-full border border-bone/20 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-bone/80"
                  data-testid="footer-trust-badge"
                >
                  <Icon className="h-3.5 w-3.5 text-terra" /> {label}
                </span>
              ))}
            </div>
          </div>
          <div className="md:col-span-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-terra">Systems</p>
            <ul className="mt-4 space-y-2.5 text-sm">
              {CATEGORIES.map((c) => (
                <li key={c.slug}>
                  <Link to={`/products/${c.slug}`} data-testid={`footer-link-${c.slug}`} className="text-bone/70 transition-colors hover:text-terra">
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-terra">Company</p>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li><Link to="/rebates" data-testid="footer-link-rebates" className="text-bone/70 hover:text-terra">Rebates</Link></li>
              <li><Link to="/installers" data-testid="footer-link-installers" className="text-bone/70 hover:text-terra">Our Installers</Link></li>
              <li><Link to="/trade-stcs" data-testid="footer-link-trade-stcs" className="text-bone/70 hover:text-terra">Trade STCs</Link></li>
              <li><Link to="/faq" data-testid="footer-link-faq" className="text-bone/70 hover:text-terra">FAQ</Link></li>
              <li><Link to="/book-inspection" data-testid="footer-link-inspection" className="text-bone/70 hover:text-terra">Book Inspection</Link></li>
              <li><Link to="/admin" data-testid="footer-link-admin" className="text-bone/40 hover:text-terra">Team Login</Link></li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-terra">Contact</p>
            <ul className="mt-4 space-y-3 text-sm text-bone/70">
              <li className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 shrink-0 text-terra" />
                <a href="mailto:info@melwestecoenergy.com.au" data-testid="footer-email" className="hover:text-terra">info@melwestecoenergy.com.au</a>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="h-4 w-4 shrink-0 text-terra" />
                <a href="tel:1300768360" data-testid="footer-phone" className="hover:text-terra">1300 768 360</a>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-terra" />
                <span>Greater Melbourne &amp; regional Victoria</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-14 flex flex-col gap-2 border-t border-bone/10 pt-6 text-xs text-bone/50 sm:flex-row sm:items-center sm:justify-between">
          <span data-testid="footer-copyright">© {new Date().getFullYear()} Melwest Eco Energy</span>
          <span>
            Rebate guidance only — official rules at{" "}
            <a href="https://www.solar.vic.gov.au" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-terra">
              solar.vic.gov.au
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
}

function StickyQuote() {
  return (
    <div className="fixed inset-x-4 bottom-4 z-40 flex gap-2 md:hidden" data-testid="sticky-quote-bar">
      <Link
        to="/contact"
        data-testid="sticky-get-quote"
        className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-terra px-5 py-3.5 text-sm font-bold text-bone shadow-lg shadow-forest-900/40"
      >
        <CalendarCheck className="h-4 w-4" /> Get a Quote
      </Link>
      <Link
        to="/book-inspection"
        data-testid="sticky-book-inspection"
        className="inline-flex items-center justify-center gap-2 rounded-full border border-bone/25 bg-forest-700 px-5 py-3.5 text-sm font-bold text-bone shadow-lg shadow-forest-900/40"
      >
        Book Inspection
      </Link>
    </div>
  );
}

export function SiteLayout() {
  return (
    <div className="min-h-screen bg-bone">
      <Navbar />
      <main className="pb-20 md:pb-0">
        <Outlet />
      </main>
      <Footer />
      <StickyQuote />
    </div>
  );
}
